import { AiCreditBalanceLevel } from "@repo/app-types/entities/creditUsage";
import { AI_CREDITS_USED_UP_MESSAGE } from "@repo/app-types/helpers/billing/aiCredits";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Link } from "@repo/ui/Link/index";
import { SimpleModalVariant } from "@repo/ui/Modal/modal";
import { DateTime } from "luxon";
import type { FC } from "react";
import {
  AI_CREDITS_CONTACT_HREF,
  type AiCreditsWarningLevel,
} from "../../billing/aiCreditsLimitBanner.js";

interface AiCreditsBillingPreviewBannerProps {
  /** Start of the first billing period that charges AI credits. */
  billingStartsAt: DateTime;
}

/** The billing page's notice that AI credit usage is tracked but not yet billed. */
export const AiCreditsBillingPreviewBanner: FC<
  AiCreditsBillingPreviewBannerProps
> = (props) => {
  const billingStartDate = props.billingStartsAt.toFormat("MMMM d");

  return (
    <InfoBanner
      title={`AI credit billing starts ${billingStartDate}`}
      description={`Usage is currently tracked but not billed. Starting ${billingStartDate}, usage counts toward your monthly credits, and credit-based features pause when credits run out.`}
    />
  );
};

interface AiCreditsIncludedBannerProps {
  /** ISO calendar day the included usage is expected to end. */
  endsOn: string;
}

/** The billing page's notice that a pilot's or an opt-out's AI credits are included. */
export const AiCreditsIncludedBanner: FC<AiCreditsIncludedBannerProps> = (
  props,
) => {
  const endsOn = DateTime.fromISO(props.endsOn, { zone: "utc" }).endOf("day");
  // A past day is left out; the status outlives it.
  const through =
    DateTime.utc() <= endsOn
      ? ` through ${endsOn.toFormat("MMMM d, yyyy")}`
      : "";

  return (
    <InfoBanner
      title="AI credits are included"
      description={`AI credits are included${through}. You won't be charged for AI credit usage, and credit-based features won't pause during this period.`}
    />
  );
};

interface AiCreditsBalanceBannerProps {
  level: AiCreditsWarningLevel;
  /** Exclusive ISO end of the current period. */
  resetsAt: string;
}

/** The billing page's notice that AI credits are low or used up. */
export const AiCreditsBalanceBanner: FC<AiCreditsBalanceBannerProps> = (
  props,
) => {
  const { level, resetsAt } = props;
  const contactUs = <Link href={AI_CREDITS_CONTACT_HREF}>Contact us</Link>;

  switch (level) {
    case AiCreditBalanceLevel.RunningLow: {
      const resetDate = DateTime.fromISO(resetsAt, { zone: "utc" }).toFormat(
        "MMMM d",
      );
      return (
        <InfoBanner
          title="AI credits may run out before the next reset"
          description={
            <>
              At the current rate, credits may run out before {resetDate}.{" "}
              {contactUs} to purchase more credits.
            </>
          }
          variant="CAUTION"
        />
      );
    }
    case AiCreditBalanceLevel.Exhausted:
      return (
        <InfoBanner
          title={AI_CREDITS_USED_UP_MESSAGE}
          description={
            <>
              Credit-using features are paused until credits reset or more are
              added. {contactUs} to purchase more credits.
            </>
          }
          variant={SimpleModalVariant.Warning}
        />
      );
  }
};
